import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Badge,
  Collapse,
  Paper,
  useTheme,
} from '@mui/material';
import {
  Dashboard,
  Inventory,
  MoveToInbox,
  Assignment,
  People,
  Person,
  Receipt,
  Assessment,
  Settings,
  PointOfSale,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import logo from '../assets/tienda-logo-alpha.png';
import UserAvatar from './UserAvatar';
import useResponsive from '../hooks/useResponsive';

const MobileDrawer = styled(Drawer)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 2,
  '& .MuiDrawer-paper': {
    width: '85%',
    maxWidth: 320,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '0 16px 16px 0',
    zIndex: theme.zIndex.drawer + 2,
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  position: 'relative',
}));

const StyledListItem = styled(ListItemButton)(({ theme, active }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  minHeight: 56,
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '& .MuiListItemIcon-root': {
    minWidth: 40,
    color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  },
  '& .MuiListItemText-primary': {
    fontWeight: active ? 600 : 400,
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
  },
}));

const menuItems = [
  {
    text: 'Dashboard',
    icon: <Dashboard />,
    page: 'Dashboard',
    category: 'main',
  },
  {
    text: 'POS',
    icon: <PointOfSale />,
    page: 'POS',
    category: 'main',
    badge: 'NEW',
  },
  {
    text: 'Products',
    icon: <Inventory />,
    page: 'Products',
    category: 'inventory',
  },
  {
    text: 'Product Movements',
    icon: <MoveToInbox />,
    page: 'Product Movements',
    category: 'inventory',
  },
  {
    text: 'Inventory Closing',
    icon: <Assignment />,
    page: 'Inventory Closing',
    category: 'inventory',
  },
  {
    text: 'Customers',
    icon: <People />,
    page: 'Customers',
    category: 'people',
  },
  {
    text: 'Users',
    icon: <Person />,
    page: 'Users',
    category: 'people',
  },
  {
    text: 'Transactions',
    icon: <Receipt />,
    page: 'Transactions',
    category: 'sales',
  },
  {
    text: 'Reports',
    icon: <Assessment />,
    page: 'Reports',
    category: 'sales',
  },
  {
    text: 'Settings',
    icon: <Settings />,
    page: 'Settings',
    category: 'system',
  },
];

const MobileNavigation = ({ 
  user, 
  currentPage, 
  onNavigate, 
  onLogout,
  drawerOpen,
  onDrawerClose,
  onProfileMenuOpen 
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [expandedCategories, setExpandedCategories] = useState({
    main: true,
    inventory: false,
    people: false,
    sales: false,
    system: false,
  });

  const handleCategoryToggle = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleNavigate = (page) => {
    onNavigate(page);
    onDrawerClose();
  };

  const categorizedItems = menuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const categories = {
    main: { title: 'Main', icon: <Dashboard /> },
    inventory: { title: 'Inventory', icon: <Inventory /> },
    people: { title: 'People', icon: <People /> },
    sales: { title: 'Sales', icon: <Receipt /> },
    system: { title: 'System', icon: <Settings /> },
  };

  if (!isMobile) {
    return null; // Use regular desktop navigation
  }

  return (
    <>
      {/* Mobile Drawer Navigation */}
      <MobileDrawer
        anchor="left"
        open={drawerOpen}
        onClose={onDrawerClose}
        variant="temporary"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        <DrawerHeader>
          <img
            src={logo}
            alt="Logo"
            style={{ height: 32, width: 'auto', marginRight: 12 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600 }}>
              {user?.entity_name || 'Tienda One'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {user?.username}
            </Typography>
          </Box>
          <IconButton
            onClick={onDrawerClose}
            sx={{ color: 'inherit' }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>

        <Box sx={{ overflow: 'auto', pb: 8 }}>
          {Object.entries(categories).map(([categoryKey, category]) => {
            const items = categorizedItems[categoryKey] || [];
            if (items.length === 0) return null;

            return (
              <Box key={categoryKey}>
                <ListItem
                  button
                  onClick={() => handleCategoryToggle(categoryKey)}
                  sx={{ py: 1, px: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={category.title}
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  />
                  {expandedCategories[categoryKey] ? <ExpandLess /> : <ExpandMore />}
                </ListItem>

                <Collapse in={expandedCategories[categoryKey]} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {items.map((item) => (
                      <StyledListItem
                        key={item.text}
                        onClick={() => handleNavigate(item.page)}
                        active={currentPage.toLowerCase() === item.page.toLowerCase()}
                      >
                        <ListItemIcon>
                          {item.badge ? (
                            <Badge badgeContent={item.badge} color="error" variant="dot">
                              {item.icon}
                            </Badge>
                          ) : (
                            item.icon
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                          }}
                        />
                      </StyledListItem>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })}

          <Divider sx={{ my: 2 }} />

          {/* User Profile Section */}
          <ListItem
            button
            onClick={onProfileMenuOpen}
            sx={{ mx: 1, borderRadius: 1 }}
          >
            <ListItemIcon>
              <UserAvatar 
                seed={user?.email || user?.username || 'default'} 
                size={32}
                style="thumbs"
              />
            </ListItemIcon>
            <ListItemText 
              primary="Profile & Settings"
              secondary="Manage your account"
              primaryTypographyProps={{
                fontSize: '0.875rem',
              }}
              secondaryTypographyProps={{
                fontSize: '0.75rem',
              }}
            />
          </ListItem>
        </Box>
      </MobileDrawer>
    </>
  );
};

export default MobileNavigation;